import { Enrollment } from '../types';
import { supabase } from '../lib/supabase';

// Maps an enrollments row to the Enrollment type
function mapEnrollment(row: any): Enrollment {
  return {
    courseId: row.course_id,
    progress: row.progress,
    completedLessonIds: row.completed_lesson_ids || [],
    lastAccessedLessonId: row.last_accessed_lesson_id || undefined,
    enrolledAt: new Date(row.enrolled_at),
    expiresAt: row.expires_at ? new Date(row.expires_at) : undefined,
    sourceBundleId: row.source_bundle_id || undefined,
  };
}

/** True when a time-limited (bundle-granted) enrollment has lapsed. */
export function isEnrollmentExpired(enrollment: Pick<Enrollment, 'expiresAt'>): boolean {
  return !!enrollment.expiresAt && enrollment.expiresAt.getTime() < Date.now();
}

// Pre-20260901000003 direct-insert path, kept only for the deploy window where
// the app is live before the enroll_in_course RPC exists. Once the migration
// is applied the "Students can enroll" INSERT policy is gone, so this path
// can only fail — delete it (and its call site) after the prod db push.
async function legacyEnrollInCourse(courseId: string): Promise<Enrollment> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: existingEnrollment } = await supabase
    .from('enrollments')
    .select('*')
    .eq('user_id', user.id)
    .eq('course_id', courseId)
    .maybeSingle();

  if (existingEnrollment) {
    return mapEnrollment(existingEnrollment);
  }

  // Mirror the RPC's gate so the fallback is never weaker than the server
  // path it stands in for: a priced course refuses (non-selling tenants have
  // prices zeroed by 20260616000001, so free-enrol tenants pass), and a
  // course RLS hides (cross-tenant, or unpublished for a student) reads as
  // not found. Client-side only — but during the window this covers, the old
  // INSERT policy is still live, so this is exactly the pre-RPC status quo
  // with the click-handler's check moved into the one write path.
  const { data: course } = await supabase
    .from('courses')
    .select('id, price')
    .eq('id', courseId)
    .maybeSingle();
  if (!course) throw new Error('Course not found');
  if ((course.price ?? 0) > 0) {
    throw new Error('payment_required: this course must be purchased');
  }

  const { data: profile } = await supabase
    .from('users')
    .select('tenant_id')
    .eq('id', user.id)
    .single();

  const { data, error } = await supabase
    .from('enrollments')
    .insert({
      user_id: user.id,
      course_id: courseId,
      tenant_id: profile?.tenant_id,
      progress: 0,
      completed_lesson_ids: []
    })
    .select()
    .single();

  if (error) {
    // Duplicate-insert race: fetch the row the other write created
    if (error.code === '23505') {
      const { data: raceEnrollment } = await supabase
        .from('enrollments')
        .select('*')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .single();

      if (raceEnrollment) {
        return mapEnrollment(raceEnrollment);
      }
    }
    throw new Error(error.message);
  }

  return mapEnrollment(data);
}

/**
 * Enrollment lifecycle + progress. Split out of courseService (June 2026);
 * courseService spreads these methods for backward compatibility, but new
 * code should import enrollmentService directly.
 */
export const enrollmentService = {
  async getMyEnrollments(): Promise<Enrollment[]> {
    const { data, error } = await supabase
      .from('enrollments')
      .select('*')
      .order('enrolled_at', { ascending: false });

    if (error) throw new Error(error.message);

    return data.map(mapEnrollment);
  },

  async enrollInCourse(courseId: string): Promise<Enrollment> {
    // Server-side gate (20260901000003): verifies free-or-purchased so a
    // hand-crafted insert can't join a paid course. Idempotent — returns the
    // existing row untouched when already enrolled.
    const { data, error } = await supabase.rpc('enroll_in_course', {
      p_course_id: courseId,
    });

    if (error) {
      // Deploy-gap fallback: the app can reach prod before the migration does
      // (Vercel auto-deploys on merge; db push is a separate step). PGRST202 =
      // function not in the schema cache. Remove once 20260901000003 is live.
      if (error.code === 'PGRST202') {
        return legacyEnrollInCourse(courseId);
      }
      throw new Error(error.message);
    }

    return mapEnrollment(data);
  },

  async completeLesson(courseId: string, lessonId: string): Promise<Enrollment> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: enrollment, error: fetchError } = await supabase
      .from('enrollments')
      .select('*')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .single();

    if (fetchError) throw new Error(fetchError.message);

    if (enrollment.completed_lesson_ids?.includes(lessonId)) {
      // Recalculate progress in case it drifted (e.g. 96% when all lessons are done)
      let recalcProgress = enrollment.progress;
      const { data: courseData } = await supabase
        .from('courses')
        .select(`sections(lessons(id, type, content))`)
        .eq('id', courseId)
        .single();
      if (courseData) {
        const allLessons = courseData.sections.flatMap((s: any) => s.lessons)
          .filter((l: any) => l.type !== 'HEADING');
        const allIds = allLessons.map((l: any) => l.id);
        if (allIds.length > 0 && allIds.every((id: string) => enrollment.completed_lesson_ids.includes(id))) {
          recalcProgress = 100;
        }
      }

      const updatePayload: Record<string, any> = { last_accessed_lesson_id: lessonId };
      if (recalcProgress !== enrollment.progress) {
        updatePayload.progress = recalcProgress;
      }

      const { data: updated, error: updateError } = await supabase
        .from('enrollments')
        .update(updatePayload)
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .select()
        .single();

      if (updateError) throw new Error(updateError.message);

      return mapEnrollment(updated);
    }

    const updatedCompletedIds = [...(enrollment.completed_lesson_ids || []), lessonId];

    const { data: course } = await supabase
      .from('courses')
      .select(`sections(lessons(id, type, content))`)
      .eq('id', courseId)
      .single();

    let progress = 0;
    if (course) {
      const allLessons = course.sections.flatMap((section: any) => section.lessons)
        .filter((l: any) => l.type !== 'HEADING');
      // Count slides per lesson for weighted progress
      const getSlideCount = (lesson: any): number => {
        const c = lesson.content;
        if (c && typeof c === 'object' && 'version' in c && c.version === 2 && Array.isArray(c.slides)) {
          return Math.max(c.slides.length, 1);
        }
        return 1; // non-slide lessons count as 1
      };
      const totalSlides = allLessons.reduce((acc: number, l: any) => acc + getSlideCount(l), 0);
      const completedSlides = allLessons
        .filter((l: any) => updatedCompletedIds.includes(l.id))
        .reduce((acc: number, l: any) => acc + getSlideCount(l), 0);
      progress = totalSlides > 0 ? Math.round((completedSlides / totalSlides) * 100) : 0;
      // Force to 100% when all lessons are completed (avoids rounding drift)
      const allLessonIds = allLessons.map((l: any) => l.id);
      if (allLessonIds.length > 0 && allLessonIds.every((id: string) => updatedCompletedIds.includes(id))) {
        progress = 100;
      }
    }

    const { data: updated, error: updateError } = await supabase
      .from('enrollments')
      .update({
        completed_lesson_ids: updatedCompletedIds,
        progress,
        last_accessed_lesson_id: lessonId
      })
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .select()
      .single();

    if (updateError) throw new Error(updateError.message);

    return mapEnrollment(updated);
  },
};
